import * as Cesium from 'cesium'
import { mapGetters } from 'vuex'

export const CesiumInitialization = {
  data() {
    return {
      viewer: null,
      mapboxToken:
        'pk.eyJ1IjoiZXhvdHJhaWwiLCJhIjoiY2szcTNnZmFkMDkzbzNtcDhzMm0ydmJ5YyJ9.5so1MY_ou6yoPmLAzLchUg',
      colClass: '',
      marginClass: '',
      icrfToFixedlastUpdateDate: new Cesium.JulianDate()
    }
  },
  computed: {
    ...mapGetters({
      userSettings: 'userSettings',
      defaultUserSettings: 'defaultUserSettings',
      localDefaultUserSettings: 'localDefaultUserSettings'
    })
  },
  methods: {
    createCesiumImageLayerWithoutIon() {
      return [
        {
          name: 'Mapbox Dark',
          tooltip: 'Mapbox Dark',
          iconUrl: require('../../assets/images/mapboxDark.jpg'),
          id: 'dark-v10'
        }
      ]
        .map((el) => {
          el.creationFunction = () => {
            return new Cesium.MapboxStyleImageryProvider({
              styleId: el.id,
              ellipsoid: this.ellipsoid,
              accessToken: this.mapboxToken
            })
          }
          return new Cesium.ProviderViewModel(el)
        })
        .concat(
          [
            {
              name: 'Mapbox Satellite',
              tooltip: 'Mapbox satellite tiles',
              iconUrl: Cesium.buildModuleUrl(
                'Widgets/Images/ImageryProviders/mapboxSatellite.png'
              ),
              id: 'mapbox.satellite'
            }
          ].map((el) => {
            el.creationFunction = () => {
              return new Cesium.MapboxImageryProvider({
                mapId: el.id,
                ellipsoid: this.ellipsoid,
                accessToken: this.mapboxToken
              })
            }
            return new Cesium.ProviderViewModel(el)
          })
        )
        .concat(
          [
            {
              name: 'Toner',
              url: 'https://stamen-tiles.a.ssl.fastly.net/toner/',
              tooltip:
                'A high contrast black and white map.\nhttp://maps.stamen.com',
              iconUrl: require('../../assets/images/toner_map_icon.png')
            }
          ]
            .map((el) => {
              el.creationFunction = () => {
                return new Cesium.OpenStreetMapImageryProvider({
                  url: el.url,
                  credit: el.name,
                  ellipsoid: this.ellipsoid
                })
              }
              return new Cesium.ProviderViewModel(el)
            })
            .concat(
              [
                {
                  name: 'OSM Streets',
                  tooltip: 'OSM streets tiles',
                  iconUrl: Cesium.buildModuleUrl(
                    'Widgets/Images/ImageryProviders/openStreetMap.png'
                  ),
                  id: 'osmStreets'
                }
              ].map((el) => {
                el.creationFunction = () => {
                  return new Cesium.OpenStreetMapImageryProvider({
                    iconUrl: el.iconUrl,
                    credit: el.name,
                    ellipsoid: this.ellipsoid
                  })
                }
                return new Cesium.ProviderViewModel(el)
              })
            )
        )
    },

    createCesiumImageLayerOffline() {
      return [
        {
          url: Cesium.buildModuleUrl('Assets/Textures/NaturalEarthII'),
          name: 'Natural Earth II',
          tooltip: 'Natural Earth II tiles',
          iconUrl: Cesium.buildModuleUrl(
            'Widgets/Images/ImageryProviders/naturalEarthII.png'
          ),
          id: 'naturalEarthII'
        }
      ].map((el) => {
        el.creationFunction = () => {
          return new Cesium.TileMapServiceImageryProvider({
            url: el.url,
            credit: el.name,
            ellipsoid: this.ellipsoid
          })
        }
        return new Cesium.ProviderViewModel(el)
      })
    },

    initializeViewer(
      selectionEnabled = false,
      animationEnabled = false,
      sceneModePickerEnabled = true,
      geocoder = true,
      requestRenderModeEnabled = false
    ) {
      const offlineConfig =
        this.userSettings?.standardConfigurations?.['Cesium offline'] ??
        this.defaultUserSettings?.standardConfigurations?.['Cesium offline'] ??
        this.localDefaultUserSettings?.standardConfigurations?.[
          'Cesium offline'
        ] ??
        false
      const offline = offlineConfig === true
      /**
       * This class is an example of a custom geocoder. It provides geocoding through the OpenStreetMap Nominatim service.
       * @alias OpenStreetMapNominatimGeocoder
       * @constructor
       */
      class OpenStreetMapNominatimGeocoder {
        constructor() {}
        /**
         * The function called to geocode using this geocoder service.
         *
         * @param {String} input The query to be sent to the geocoder service
         * @returns {Promise<GeocoderService.Result[]>}
         */
        async geocode(input) {
          let endpoint = 'https://nominatim.openstreetmap.org/search'
          let resource = new Cesium.Resource({
            url: endpoint,
            queryParameters: {
              format: 'json',
              q: input
            }
          })

          const results = await resource.fetchJson()
          let bboxDegrees
          return results.map(function (resultObject) {
            bboxDegrees = resultObject.boundingbox
            return {
              displayName: resultObject.display_name,
              destination: Cesium.Rectangle.fromDegrees(
                bboxDegrees[2],
                bboxDegrees[0],
                bboxDegrees[3],
                bboxDegrees[1]
              )
            }
          })
        }
      }
      const cesiumContainer = this.$refs.cesiumContainer
      const formColumn =
        this.$parent.$el.getElementsByClassName('form-column')[0]

      this.viewer = new Cesium.Viewer(cesiumContainer, {
        imageryProviderViewModels: offline
          ? this.createCesiumImageLayerOffline()
          : this.createCesiumImageLayerWithoutIon(),
        terrainProviderViewModels: [],
        terrainProvider: null,
        animation: animationEnabled,
        timeline: animationEnabled,
        baseLayerPicker: true,
        homeButton: true,
        navigationHelpButton: false,
        sceneModePicker: sceneModePickerEnabled,
        requestRenderMode: requestRenderModeEnabled,
        fullscreenButton: true,
        CreditDisplay: false,
        selectionIndicator: selectionEnabled,
        infoBox: selectionEnabled,
        skyBox: false,
        // mapProjection: new Cesium.WebMercatorProjection(),
        geocoder:
          geocoder && !offline ? new OpenStreetMapNominatimGeocoder() : false
      })
      let creditLogoContainer = cesiumContainer.querySelector(
        '.cesium-credit-logoContainer'
      )
      creditLogoContainer.style.display = 'none'
      this.viewer.fullscreenButton.viewModel.command.afterExecute.addEventListener(
        () => this.toggleFullscreen(cesiumContainer, formColumn)
      )
      document.addEventListener('fullscreenchange', (__) => {
        if (!this.viewer.fullscreenButton.viewModel.isFullscreen) {
          this.disableFullScreen(cesiumContainer, formColumn)
        }
      })
    },

    deactivateLook() {
      this.viewer.scene.screenSpaceCameraController.enableLook = false
    },

    deactivateTilt() {
      this.viewer.scene.screenSpaceCameraController.enableTilt = false
    },

    deactivateRotate() {
      this.viewer.scene.screenSpaceCameraController.enableRotate = false
    },

    deactivateTranslate() {
      this.viewer.scene.screenSpaceCameraController.enableTranslate = false
    },

    deactivateZoom() {
      this.viewer.scene.screenSpaceCameraController.enableZoom = false
    },

    deactivateControl(type = 'Look') {
      this.viewer.scene.screenSpaceCameraController['enable' + type] = false
    },

    activateControl(type = 'Look') {
      this.viewer.scene.screenSpaceCameraController['enable' + type] = true
    },

    deactivateControls() {
      this.deactivateTilt()
      this.deactivateLook()
    },

    deactivateAllControls() {
      this.deactivateLook()
      this.deactivateTilt()
      this.deactivateRotate()
      this.deactivateTranslate()
      this.deactivateZoom()
    },

    computeIcrfToFixed(clock, force = false) {
      const timePassed = Cesium.JulianDate.secondsDifference(
        clock.currentTime,
        this.icrfToFixedlastUpdateDate
      )

      // if time passed > 1 second of real time (or if update is forced):
      if (Math.abs(timePassed) > 1 || force) {
        this.icrfToFixed = Cesium.Transforms.computeIcrfToFixedMatrix(
          clock.currentTime
        )
        this.icrfToFixedlastUpdateDate = clock.currentTime
      }
    },

    onMorphComplete() {
      if (this.form && this.form.morphButtonDisabled) {
        this.form.morphButtonDisabled = false
      }
      const toggleUserInteraction = (value) => {
        this.viewer.scene.screenSpaceCameraController.enableRotate = value
        this.viewer.scene.screenSpaceCameraController.enableTranslate = value
        this.viewer.scene.screenSpaceCameraController.enableZoom = value
        this.viewer.scene.screenSpaceCameraController.enableTilt = value
        this.viewer.scene.screenSpaceCameraController.enableLook = value
      }
      if (this.viewer.scene.mode === Cesium.SceneMode.SCENE3D) {
        this.rotatingOrbitalPlanes.show = true
        this.viewer.scene.postUpdate.addEventListener(this.makeEarthSpin)
        toggleUserInteraction(true)
      } else {
        this.viewer.scene.postUpdate.removeEventListener(this.makeEarthSpin)
        this.rotatingOrbitalPlanes.show = false
        toggleUserInteraction(false)
      }
      this.viewer.clock.shouldAnimate = true
    },

    /**
     * Create the illusion that Earth is rotating by rotating camera and orbital planes
     */
    makeEarthSpin(scene, __) {
      if (scene.mode !== Cesium.SceneMode.SCENE3D) {
        return
      }

      if (Cesium.defined(this.icrfToFixed)) {
        const camera = this.viewer.camera
        const offset = Cesium.Cartesian3.clone(camera.position)
        const transform = Cesium.Matrix4.fromRotationTranslation(
          this.icrfToFixed
        )
        camera.lookAtTransform(transform, offset)
        for (let i = 0; i < this.rotatingOrbitalPlanes.length; i++) {
          const polylinePrimitive = this.rotatingOrbitalPlanes.get(i)
          if (polylinePrimitive && polylinePrimitive.modelMatrix) {
            Cesium.Matrix4.fromRotationTranslation(
              this.icrfToFixed,
              Cesium.Cartesian3.ZERO,
              polylinePrimitive.modelMatrix
            )
          }
        }
      }
    },

    toggleFullscreen(cesiumContainer, formColumn) {
      if (!this.viewer.fullscreenButton.viewModel.isFullscreen) {
        this.enableFullScreen(cesiumContainer, formColumn)
      } else {
        this.disableFullScreen(cesiumContainer, formColumn)
      }
    },

    enableFullScreen(cesiumContainer, formColumn) {
      document.body.style.overflow = 'none'
      this.previousPosition = cesiumContainer.style.position
      cesiumContainer.style.position = 'fixed'
      this.previousTop = cesiumContainer.style.top
      cesiumContainer.style.top = '0px'
      this.previousLeft = cesiumContainer.style.left
      cesiumContainer.style.left = '0px'
      this.previousWidth = cesiumContainer.style.width
      cesiumContainer.style.width = '100%'
      this.previousHeight = cesiumContainer.style.height
      cesiumContainer.style.height = '100%'
      this.previousZIndex = cesiumContainer.style.zIndex
      cesiumContainer.style.zIndex = '999'
      this.previousMaxHeight = cesiumContainer.style.maxHeight
      cesiumContainer.style.maxHeight = ''
      if (formColumn) {
        this.colClass = formColumn.classList.value.match(/col-\d/)[0]
        formColumn.classList.add('fullscreen-form-column')
        formColumn.classList.value = formColumn.classList.value.replace(
          this.colClass,
          'col-3'
        )
      }
      if (cesiumContainer.classList.value.includes('mt')) {
        this.marginClass = cesiumContainer.classList.value.match(/mt-\d/)[0]
        cesiumContainer.classList.remove(this.marginClass)
      }
    },
    disableFullScreen(cesiumContainer, formColumn) {
      document.body.style.overflow = 'auto'
      cesiumContainer.style.position = this.previousPosition
      cesiumContainer.style.top = this.previousTop
      cesiumContainer.style.left = this.previousLeft
      cesiumContainer.style.width = this.previousWidth
      cesiumContainer.style.height = this.previousHeight
      cesiumContainer.style.zIndex = this.previousZIndex
      cesiumContainer.style.maxHeight = this.previousMaxHeight
      cesiumContainer.style.margin = ''
      if (formColumn) {
        cesiumContainer.style.maxHeight = '500px'
        formColumn.classList.remove('fullscreen-form-column')
        formColumn.classList.value = formColumn.classList.value.replace(
          'col-3',
          this.colClass
        )
      }
      if (this.marginClass.length > 0) {
        cesiumContainer.classList.add(this.marginClass)
      }
    },

    insertElementInToolbar(
      button,
      cesiumContainer = this.$refs.cesiumContainer
    ) {
      if (!cesiumContainer || !button) {
        return
      }
      const toolbar = cesiumContainer.querySelector(
        '.cesium-viewer .cesium-viewer-toolbar'
      )
      const buttonToAppendBefore = cesiumContainer.querySelector(
        ' .cesium-viewer .cesium-viewer-toolbar'
      ).children[0]

      if (toolbar && buttonToAppendBefore && button) {
        toolbar.insertBefore(button, buttonToAppendBefore)
      }
    },

    setCesiumClock(startDate, stopDate, multiplier = 1) {
      this.viewer.clock.startTime = startDate
      this.viewer.clock.stopTime = stopDate
      this.viewer.clock.multiplier = multiplier
      this.viewer.clock.shouldAnimate = multiplier === 1 ? false : true
      this.viewer.clock.clockRange = Cesium.ClockRange.LOOP_STOP
      this.viewer.clock.currentTime = startDate
      if (this.viewer.timeline) {
        this.viewer.timeline.zoomTo(startDate, stopDate)
      }
    },

    hideSceneModePicker() {
      document
        .querySelectorAll('.cesium-sceneModePicker-wrapper')
        .forEach((el) => {
          el.style.display = 'none'
        })
    },

    toggleTimeline(show) {
      if (!show) {
        this.creditsLeftOffset = document.getElementsByClassName(
          'cesium-viewer-bottom'
        )[0].style.left
      }
      let display = show ? 'block' : 'none'
      this.viewer.animation.container.style.display = display
      this.viewer.timeline.container.style.display = display
      this.viewer.forceResize()
      let offsetLeft = show ? `${this.creditsLeftOffset}px` : '0px'
      document.getElementsByClassName('cesium-viewer-bottom')[0].style.left =
        offsetLeft
    }
  }
}
